'use client';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Link from 'next/link';
import { useI18n, useDirection } from '@/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Menu, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function Navbar() {
  const { t } = useI18n();
  const { isRTL } = useDirection();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: t.nav.features, href: '/features' },
    { label: t.nav.pricing, href: '/#pricing' },
    { label: t.nav.about, href: '/#about' },
  ];

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${isScrolled
          ? 'bg-background/95 backdrop-blur-sm border-b border-border'
          : 'bg-transparent'
          }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center">
              <img
                src="https://deepbuild-v1.s3.ap-south-1.amazonaws.com/uploads/1773435501096-ynivdg6ty3-4.png"
                alt={t.nav.logoAlt}
                className="h-10 sm:h-14 lg:h-56 w-auto object-contain"
              />
            </a>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Desktop actions */}
            <div className="hidden lg:flex items-center gap-4">
              <LanguageSwitcher />
              <Button asChild variant="ghost" className="text-sm text-muted-foreground">
                <Link href="/login">{t.nav.login}</Link>
              </Button>
              <Button asChild className="text-sm bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link href="/register">
                  {t.nav.getStarted}
                  <ArrowIcon className="w-4 h-4 ms-2" />
                </Link>
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-border"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div
              className="absolute inset-0 bg-background/95 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="absolute top-20 left-4 right-4 p-6 bg-card border border-border rounded-2xl shadow-lg"
            >
              <nav className="space-y-4 mb-6">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block py-2 text-base text-foreground"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>

              <div className="pt-4 border-t border-border space-y-3">
                <Button asChild variant="outline" className="w-full">
                  <Link href="/login">{t.nav.login}</Link>
                </Button>
                <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Link href="/register">
                    {t.nav.getStarted}
                    <ArrowIcon className="w-4 h-4 ms-2" />
                  </Link>
                </Button>
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <LanguageSwitcher />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
